import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface UserDetails {
  [key: string]: any;
}

interface AuthContextType {
  token: string | null;
  userDetails: UserDetails | null;
  isLoggedIn: boolean;
  lastLoginAt: string | null;
  login: (
    token: string,
    userDetails: UserDetails | null,
    loginTimestamp?: string | null,
  ) => void;
  updateUserDetails: (userDetails: UserDetails | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  userDetails: null,
  isLoggedIn: false,
  lastLoginAt: null,
  login: () => {},
  updateUserDetails: () => {},
  logout: () => {},
});

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [token, setToken] = useState<string | null>(() =>
    sessionStorage.getItem("authToken"),
  );
  const [userDetails, setUserDetails] = useState<UserDetails | null>(() => {
    const storedUserDetails = sessionStorage.getItem("userDetails");
    if (!storedUserDetails) return null;

    try {
      return JSON.parse(storedUserDetails);
    } catch {
      sessionStorage.removeItem("userDetails");
      return null;
    }
  });
  const [lastLoginAt, setLastLoginAt] = useState<string | null>(() => {
    const storedLoginTime = sessionStorage.getItem("lastLoginAt");
    if (storedLoginTime) return storedLoginTime;

    const storedUserDetails = sessionStorage.getItem("userDetails");
    if (!storedUserDetails) return null;

    try {
      const parsedDetails = JSON.parse(storedUserDetails) as UserDetails;
      const candidateTime =
        parsedDetails?.last_login ||
        parsedDetails?.lastLogin ||
        parsedDetails?.last_login_at ||
        parsedDetails?.login_time ||
        parsedDetails?.loginTime;
      return candidateTime ? String(candidateTime) : null;
    } catch {
      sessionStorage.removeItem("userDetails");
      return null;
    }
  });

  useEffect(() => {
    const storedToken = sessionStorage.getItem("authToken");
    const storedUserDetails = sessionStorage.getItem("userDetails");
    const storedLoginTime = sessionStorage.getItem("lastLoginAt");

    setToken(storedToken);
    setLastLoginAt(storedLoginTime || null);

    if (storedUserDetails) {
      try {
        setUserDetails(JSON.parse(storedUserDetails));
      } catch {
        setUserDetails(null);
      }
    } else {
      setUserDetails(null);
    }
  }, []);

  const updateUserDetails = useCallback((details: UserDetails | null) => {
    if (details) {
      sessionStorage.setItem("userDetails", JSON.stringify(details));
    } else {
      sessionStorage.removeItem("userDetails");
    }
    setUserDetails(details);
  }, []);

  const login = useCallback(
    (
      newToken: string,
      details: UserDetails | null,
      loginTimestamp: string | null = null,
    ) => {
      sessionStorage.setItem("authToken", newToken);

      const candidateLoginTime =
        loginTimestamp ||
        details?.last_login ||
        details?.lastLogin ||
        details?.last_login_at ||
        details?.login_time ||
        details?.loginTime ||
        new Date().toISOString();

      if (candidateLoginTime) {
        const resolvedLoginTime = String(candidateLoginTime);
        sessionStorage.setItem("lastLoginAt", resolvedLoginTime);
        setLastLoginAt(resolvedLoginTime);
      } else {
        sessionStorage.removeItem("lastLoginAt");
        setLastLoginAt(null);
      }

      updateUserDetails(details);
      setToken(newToken);
    },
    [updateUserDetails],
  );

  const logout = useCallback(() => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("userDetails");
    sessionStorage.removeItem("lastLoginAt");
    setToken(null);
    setUserDetails(null);
    setLastLoginAt(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      userDetails,
      isLoggedIn: Boolean(token),
      lastLoginAt,
      login,
      updateUserDetails,
      logout,
    }),
    [token, userDetails, lastLoginAt, login, updateUserDetails, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
